'use client';

/**
 * Browser cookie WRITES with security defaults. Reads are re-exported from get-cookie.ts,
 * not reimplemented here.
 *
 * Deliberately separate from helpers/set-cookie.ts's default export: that one takes an
 * expiry UTCString and defaults to SameSite=Strict; this one takes maxAge/expires and
 * defaults to lax, which is what the p13n cookie needs.
 */

export { getCookie } from './get-cookie';

interface CookieOptions {
  path?: string;
  domain?: string;
  maxAge?: number; // in seconds
  expires?: Date;
  secure?: boolean;
  sameSite?: 'strict' | 'lax' | 'none';
}

/** Safely set a cookie with security defaults. */
export function setCookie(name: string, value: string, options: CookieOptions = {}): void {
  if (typeof document === 'undefined') return;

  const defaults: CookieOptions = {
    path: '/',
    secure: window.location.protocol === 'https:',
    sameSite: 'lax',
    ...options,
  };

  let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

  if (defaults.path) cookieString += `; path=${defaults.path}`;
  if (defaults.domain) cookieString += `; domain=${defaults.domain}`;
  if (defaults.maxAge !== undefined) {
    cookieString += `; max-age=${defaults.maxAge}`;
  } else if (defaults.expires) {
    cookieString += `; expires=${defaults.expires.toUTCString()}`;
  }
  if (defaults.secure) cookieString += '; secure';
  if (defaults.sameSite) cookieString += `; samesite=${defaults.sameSite}`;

  document.cookie = cookieString;
}

/** Safely delete a cookie. */
export function deleteCookie(name: string, options: CookieOptions = {}): void {
  setCookie(name, '', { ...options, maxAge: -1 });
}
